import { ClientOrganizationCreateRequestTagsEnum } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useQuery } from '@tanstack/react-query';

import { API_CACHE_KEYS } from '@/features/api/constants/cache';
import { useGrapevineOrganizationApi } from '@/features/api/hooks/grapevine-organization/useGrapevineOrganizationApi';
import { useAuth } from '@/features/authorization/provider/useAuth';

const KNOWN_TAGS = new Set<string>(
  Object.values(ClientOrganizationCreateRequestTagsEnum)
);

/**
 * Loads the organization types the given creator organization is allowed to
 * create. The backend derives the set from the creator organization's tags
 * (see {@code GET /grapevine/organization/{organizationId}/creatableTypes}):
 * ADMIN-tagged organizations may create any type, territorial authorities only
 * a subset, everyone else nothing. Unknown values are filtered out so the
 * result stays a valid {@link ClientOrganizationCreateRequestTagsEnum} array.
 */
export const useGetCreatableOrganizationTypes = (organizationId: string) => {
  const { isAuthenticated } = useAuth();
  const api = useGrapevineOrganizationApi();

  return useQuery({
    queryKey: [API_CACHE_KEYS.CREATABLE_ORGANIZATION_TYPES, organizationId],
    queryFn: async () => {
      const types = await api.getCreatableOrganizationTypes({ organizationId });
      return types.filter(
        (type): type is ClientOrganizationCreateRequestTagsEnum =>
          KNOWN_TAGS.has(type)
      );
    },
    enabled: isAuthenticated && !!organizationId
  });
};
